import {
  Box,
  Table,
  Text,
  Badge,
  HStack,
  Accordion,
} from '@chakra-ui/react';
import { RESULT_STATUS_LABELS, type ResultStatus } from '../../types/script'
import type { CanvassingSession } from '../../types/contactDetail'

interface CanvassingHistorySectionProps {
  sessions: CanvassingSession[]
}

const resultColors: Record<string, string> = {
  Completed: 'green',
  Busy: 'yellow',
  NoAnswer: 'gray',
  LeftMessage: 'blue',
  Moved: 'purple',
  Refused: 'red',
  Callback: 'orange',
  Deceased: 'red',
  Inaccessible: 'orange',
  LanguageBarrier: 'teal',
}

const methodColors: Record<string, string> = {
  Door: 'blue',
  Phone: 'green',
  Event: 'purple',
  Text: 'teal',
  Email: 'cyan',
}

export function CanvassingHistorySection({ sessions }: CanvassingHistorySectionProps) {
  if (sessions.length === 0) {
    return (
      <Box bg="surface" p={8} borderRadius="lg" shadow="md" textAlign="center">
        <Text color="gray.500">No canvassing sessions recorded.</Text>
      </Box>
    )
  }

  return (
    <Box bg="surface" borderRadius="lg" shadow="md" overflow="hidden">
      <Accordion.Root multiple>
        {sessions.map((session) => (
          <Accordion.Item
            key={session.id}
            border="none"
            borderBottom="1px"
            borderColor="gray.100"
            value={session.id}>
            <Accordion.ItemTrigger py={3} px={4} _hover={{ bg: 'gray.50' }}>
              <Box flex="1">
                <HStack gap={3} wrap="wrap">
                  <Text fontSize="sm" fontWeight="medium">
                    {new Date(session.sessionDate).toLocaleDateString()}
                  </Text>
                  <Badge colorPalette={methodColors[session.contactMethod] ?? 'gray'} size="sm">
                    {session.contactMethod}
                  </Badge>
                  <Badge colorPalette={resultColors[session.resultStatus] ?? 'gray'} size="sm">
                    {RESULT_STATUS_LABELS[session.resultStatus as ResultStatus] ?? session.resultStatus}
                  </Badge>
                  <Text fontSize="sm" color="gray.500">
                    by {session.canvasserName}
                  </Text>
                  {session.scriptName && (
                    <Text fontSize="xs" color="gray.400">
                      Script: {session.scriptName}
                    </Text>
                  )}
                </HStack>
              </Box>
              <Accordion.ItemIndicator />
            </Accordion.ItemTrigger>
            <Accordion.ItemContent pb={4} px={4} bg="gray.50"><Accordion.ItemBody>
                {session.notes && (
                  <Box mb={3}>
                    <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>Notes</Text>
                    <Text fontSize="sm">{session.notes}</Text>
                  </Box>
                )}
                {session.surveyResponses.length > 0 && (
                  <Box>
                    <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={2}>
                      Survey Responses
                    </Text>
                    <Box overflowX="auto">
                    <Table.Root size="sm" variant="line">
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader>Question</Table.ColumnHeader>
                          <Table.ColumnHeader>Answer</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {session.surveyResponses.map((sr) => (
                          <Table.Row key={sr.id}>
                            <Table.Cell fontSize="sm">{sr.questionLabel}</Table.Cell>
                            <Table.Cell fontSize="sm">{sr.answerText || sr.responseText || '-'}</Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                    </Box>
                  </Box>
                )}
                {!session.notes && session.surveyResponses.length === 0 && (
                  <Text fontSize="sm" color="gray.400">No additional details.</Text>
                )}
              </Accordion.ItemBody></Accordion.ItemContent>
          </Accordion.Item>
        ))}
      </Accordion.Root>
    </Box>
  );
}
